import React from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TaskStatusEnumValue } from "@backend/src/schemas/enums";
import { cn } from "@/lib/utils";

import Dialog from "@/components/common/Dialog";
import YallowInfoIcon from "@/assets/svg/YallowInfoIcon.svg";
import GreenFlag from "@/assets/svg/GreenFlag.svg";
import OrangeFlag from "@/assets/svg/OrangeFlag.svg";
import RedFlag from "@/assets/svg/RedFlag.svg";

type TaskStatus = (typeof TaskStatusEnumValue)[keyof typeof TaskStatusEnumValue];

interface TaskStatusAndProgressProps {
  status?: string;
  onStatusChange: (status: TaskStatus) => void;
  availableStatuses: TaskStatus[];
  isMilestone: boolean;
  actualProgress: number;
  expectedProgress: number;
  onProgressCommit: (value: number) => void;
  hasSubtasks: boolean;
  allowed: boolean;
  flag?: "Green" | "Red" | "Orange" | string;
  startDate?: Date | string;
}

export const TaskStatusAndProgress: React.FC<TaskStatusAndProgressProps> = ({
  status,
  onStatusChange,
  availableStatuses,
  isMilestone,
  actualProgress,
  expectedProgress,
  onProgressCommit,
  hasSubtasks,
  allowed,
  flag,
  startDate,
}) => {
  const { t } = useTranslation();
  const [localProgress, setLocalProgress] = React.useState<number>(actualProgress);
  const [scheduledError, setScheduledError] = React.useState(false);

  React.useEffect(() => {
    setLocalProgress(actualProgress);
  }, [actualProgress]);

  const getStatusColor = (s?: string) => {
    switch (s) {
      case TaskStatusEnumValue.COMPLETED:
        return "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100";
      case TaskStatusEnumValue.IN_PROGRESS:
        return "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100";
      case TaskStatusEnumValue.NOT_STARTED:
      default:
        return "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100";
    }
  };

  const getFlagBadge = () => {
    if (!flag) return null;
    const normalizedFlag = flag.toLowerCase();
    const config = {
      green: { label: t("ON_TIME"), icon: GreenFlag },
      orange: { label: t("MODERATE_DELAY"), icon: OrangeFlag },
      red: { label: t("SIGNIFICANT_DELAY"), icon: RedFlag },
    }[normalizedFlag as "green" | "orange" | "red"];

    if (!config) return null;

    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <img
              src={config.icon}
              alt={config.label}
              className="h-3.5 w-3.5 shrink-0 object-contain"
            />
          </TooltipTrigger>
          <TooltipContent className="text-xs py-1 px-2" side="top">
            {config.label}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const handleProgressCommit = (val: number) => {
    if (startDate) {
      const today = new Date();
      const todayDateOnly = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
      );

      const taskStartDate = new Date(startDate);
      const startDateOnly = new Date(
        taskStartDate.getFullYear(),
        taskStartDate.getMonth(),
        taskStartDate.getDate(),
      );

      if (startDateOnly > todayDateOnly) {
        setScheduledError(true);
        setLocalProgress(actualProgress);
        return;
      }
    }
    onProgressCommit(val);
  };

  return (
    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3 bg-slate-50/70 border border-slate-200/80 rounded-xl">
      {/* Left: Status Selector */}
      <div className="flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={!allowed || hasSubtasks}>
            <Button
              type="button"
              variant="none"
              className={cn(
                "h-8 px-3 rounded-lg text-xs font-semibold border shadow-2xs transition-all flex items-center gap-1.5",
                getStatusColor(status),
                (!allowed || hasSubtasks) && "opacity-60 cursor-not-allowed",
              )}
            >
              {getFlagBadge()}
              <span>
                {status ? t(status) : t("select_status")}
              </span>
              <ChevronDown className="w-3.5 h-3.5 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-44 p-1 rounded-xl shadow-lg border border-slate-200">
            {availableStatuses.map((s) => (
              <div
                key={s}
                onClick={() => onStatusChange(s)}
                className="flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs font-medium text-slate-700 hover:bg-slate-100 cursor-pointer transition-colors"
              >
                <span>{t(s)}</span>
                {status === s && <Check className="w-3.5 h-3.5 text-primary-600" />}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {isMilestone && (
          <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer ml-1">
            <input
              type="checkbox"
              checked={status === TaskStatusEnumValue.COMPLETED}
              disabled={!allowed}
              onChange={(e) => {
                onStatusChange(
                  e.target.checked
                    ? TaskStatusEnumValue.COMPLETED
                    : TaskStatusEnumValue.NOT_STARTED,
                );
              }}
              className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
            />
            <span>{t("completed")}</span>
          </label>
        )}
      </div>

      {/* Right: Actual & Expected Progress (When not milestone) */}
      {!isMilestone && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 text-xs">
          {/* Actual Progress */}
          <div className="flex items-center gap-2 min-w-[170px]">
            <span className="text-slate-500 font-medium whitespace-nowrap">
              {t("actual_progress")}:
            </span>
            <div className="w-24">
              <Slider
                value={[localProgress]}
                max={100}
                min={0}
                disabled={hasSubtasks || !allowed}
                onValueChange={(val) => setLocalProgress(val[0])}
                onValueCommit={(val) => handleProgressCommit(val[0])}
                className="my-1 cursor-pointer"
              />
            </div>
            <span className="font-bold text-slate-700 w-8 text-right">
              {Math.round(localProgress)}%
            </span>
          </div>

          {/* Expected Progress */}
          <div className="flex items-center gap-2 min-w-[170px]">
            <span className="text-slate-400 font-medium whitespace-nowrap">
              {t("expected_progress")}:
            </span>
            <div className="w-24">
              <Slider
                value={[Math.min(100, Math.max(0, expectedProgress || 0))]}
                max={100}
                min={0}
                disabled
                className="my-1 opacity-70 cursor-default"
              />
            </div>
            <span className="font-semibold text-slate-500 w-8 text-right">
              {Math.round(expectedProgress || 0)}%
            </span>
          </div>
        </div>
      )}

      {/* Scheduled Future Task Warning Dialog */}
      <Dialog
        isOpen={scheduledError}
        onClose={() => setScheduledError(false)}
        modalClass="rounded-xl max-w-sm p-6"
      >
        <div className="flex flex-col items-center text-center gap-3">
          <img src={YallowInfoIcon} className="w-12 h-12" alt="Warning" />
          <div
            className="text-xs text-slate-700 leading-relaxed font-medium"
            dangerouslySetInnerHTML={{ __html: t("futureTaskWarning") }}
          />
          <div className="flex w-full mt-2">
            <Button
              variant="primary"
              onClick={() => {
                setScheduledError(false);
                setLocalProgress(actualProgress);
              }}
              className="w-full text-xs font-semibold"
            >
              {t("ok")}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TaskStatusAndProgress;
